import { useMemo } from 'react';
import isNil from 'lodash/isNil';
import { useAppSelector } from '../../../redux/hooks';
import * as CohortSampleChartUI from './CohortSampleChartUI';
import {
  ICohortSampleTable,
  QueryType,
} from '../../../utils/apollo/graphqlSchema';
import useCohortCompareTableState from './useCohortCompareTableState';
import UserInspectView from '../UserInspectView';
import CohortSampleSummaryDisplay from './CohortSampleSummaryDisplay';
import { selectOpenedModalType } from '../../../redux/selectors/modal';
import { ModalSliceType } from '../../../redux/slices/modal';
import { SaveCohortModal } from '../../Cohort';
import { assertCondition } from '../../../utils/types';
import CohortSampleChartTable from './CohortSampleChartTable';
import { ChartMode } from '../types';
import styled from 'styled-components';
import { SingleSelectMenu } from '../../Dropdown';
import { useChartContext } from '../ChartContext';

interface IProps {
  analysisId: string;
  mode: ChartMode;
}

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  min-height: 0;
`;

export default ({ analysisId, mode }: IProps) => {
  const isSaveCohortModalOpen = useAppSelector((state) =>
    selectOpenedModalType(
      state,
      ModalSliceType.SaveCohortFromAnalysisId,
      analysisId,
    ),
  );

  const {
    metadata: { queryType, display, query },
    chartData,
    settings: { inspectSubjectId },
  } = useChartContext();
  assertCondition(queryType === QueryType.CohortSample);
  assertCondition(chartData.queryType === QueryType.CohortSample);
  const { subjectName, tables, summary } = chartData.data;
  const isCompactMode = mode === ChartMode.Compact;

  assertCondition(query.__typename === 'CohortSampleQuery');
  const { samplePercentage, schema } = query;

  if (tables.length === 0) {
    return null;
  }

  const isCompareTable = tables?.length > 1;

  const { compareTableOptions, compareTableKey, onSelect } =
    useCohortCompareTableState();
  const filteredTables = useMemo(() => {
    if (isCompareTable) {
      return tables
        .filter(
          (table: ICohortSampleTable) =>
            table.caption.caption === compareTableKey,
        )
        .map((table: ICohortSampleTable) => {
          return {
            ...table,
            compareTableKey,
          };
        });
    }
    return tables;
  }, [tables, compareTableKey, isCompareTable]);

  return (
    <Container>
      {!isCompactMode && (
        <CohortSampleChartUI.SampleHeader>
          {isCompareTable ? (
            <>
              <SingleSelectMenu
                options={compareTableOptions}
                value={compareTableKey}
                onChange={onSelect}
              />
              {summary && (
                <CohortSampleSummaryDisplay
                  compareTableSummary={`${summary.number} ${summary.text}`}
                />
              )}
            </>
          ) : filteredTables?.[0] ? (
            <>
              <div />
              <CohortSampleSummaryDisplay
                subjectName={subjectName}
                totalCount={filteredTables[0].summary.totalCount}
                tableSize={filteredTables[0].summary.tableSize}
                isPartialResult={
                  !isNil(samplePercentage) && samplePercentage < 100
                }
              />
            </>
          ) : null}
        </CohortSampleChartUI.SampleHeader>
      )}

      {filteredTables.map((table: ICohortSampleTable, index: number) => (
        <CohortSampleChartTable
          key={`cst-${index}`}
          analysisId={analysisId}
          table={table}
          display={display}
          mode={mode}
        />
      ))}
      {inspectSubjectId && (
        <UserInspectView analysisId={analysisId} schemaId={schema.id} />
      )}
      {isSaveCohortModalOpen && <SaveCohortModal />}
    </Container>
  );
};
